/** Parse assistant message content + metadata citations into numbered refs. */

const DOC_RE = /\[doc:([0-9a-f-]{36})(?:\/([0-9a-f-]{36}))?\]/gi
const WIKI_RE = /\[wiki:([a-z0-9/_-]+)\]/gi
const NUM_RE = /\[(\d{1,2})\]/g

/**
 * @param {{ type?: string, label?: string, document_id?: string, chunk_id?: string, slug?: string, library?: string } | null | undefined} c
 * @returns {string | null}
 */
export function citationKey(c) {
  if (!c) return null
  if (c.type === 'wiki' && c.slug) return `wiki:${c.slug}`
  if (c.document_id) return `doc:${c.document_id}:${c.chunk_id || ''}`
  return null
}

function keyFromDoc(docId, chunkId) {
  return `doc:${docId}:${chunkId || ''}`
}

function keyFromWiki(slug) {
  return `wiki:${slug}`
}

/**
 * @param {string} content
 * @returns {string}
 */
export function stripRawCitationMarkers(content) {
  return String(content || '')
    .replace(DOC_RE, '')
    .replace(WIKI_RE, '')
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/**
 * @param {string} content
 * @returns {Array<{ index: number, length: number, kind: string, docId?: string, chunkId?: string, slug?: string, num?: number }>}
 */
function findInlineMatches(content) {
  const src = String(content || '')
  const matches = []
  let m

  DOC_RE.lastIndex = 0
  while ((m = DOC_RE.exec(src)) !== null) {
    matches.push({
      index: m.index,
      length: m[0].length,
      kind: 'doc',
      docId: m[1],
      chunkId: m[2] || '',
    })
  }

  WIKI_RE.lastIndex = 0
  while ((m = WIKI_RE.exec(src)) !== null) {
    matches.push({
      index: m.index,
      length: m[0].length,
      kind: 'wiki',
      slug: m[1],
    })
  }

  NUM_RE.lastIndex = 0
  while ((m = NUM_RE.exec(src)) !== null) {
    const start = m.index
    const end = start + m[0].length
    const overlaps = matches.some((o) => !(end <= o.index || start >= o.index + o.length))
    if (!overlaps) {
      matches.push({
        index: start,
        length: m[0].length,
        kind: 'num',
        num: parseInt(m[1], 10),
      })
    }
  }

  matches.sort((a, b) => a.index - b.index)
  return matches
}

/**
 * @param {string} content
 * @param {Array<{ type?: string, label?: string, document_id?: string, chunk_id?: string, slug?: string, library?: string }>} citations
 * @returns {{ registry: Array<{ num: number, key: string, citation: object }>, segments: Array<{ type: 'text' | 'ref', value?: string, num?: number }> }}
 */
export function buildCitationRegistry(content, citations = []) {
  const metaList = Array.isArray(citations) ? citations : []
  const metaByKey = new Map()
  for (const c of metaList) {
    const k = citationKey(c)
    if (k && !metaByKey.has(k)) metaByKey.set(k, c)
  }

  const keyToNum = new Map()
  const registry = []
  const usedNums = new Set()
  let nextNum = 1

  const resolveCitation = (key, citation) =>
    citation ||
    metaByKey.get(key) ||
    (key.startsWith('wiki:')
      ? { type: 'wiki', slug: key.slice(5), label: key.slice(5) }
      : key.startsWith('doc:')
        ? (() => {
            const parts = key.slice(4).split(':')
            return {
              type: 'document',
              document_id: parts[0],
              chunk_id: parts[1] || '',
              label: parts[0],
            }
          })()
        : { type: 'document', label: key })

  const assignNum = (key, citation, preferredNum = null) => {
    if (keyToNum.has(key)) return keyToNum.get(key)
    let num = preferredNum
    if (num != null && usedNums.has(num)) num = null
    if (num == null) {
      while (usedNums.has(nextNum)) nextNum += 1
      num = nextNum
      nextNum += 1
    } else {
      nextNum = Math.max(nextNum, num + 1)
    }
    usedNums.add(num)
    keyToNum.set(key, num)
    registry.push({ num, key, citation: resolveCitation(key, citation) })
    return num
  }

  const matches = findInlineMatches(content)
  const segments = []
  let cursor = 0

  for (const match of matches) {
    if (match.index > cursor) {
      segments.push({ type: 'text', value: content.slice(cursor, match.index) })
    }

    if (match.kind === 'doc') {
      const key = keyFromDoc(match.docId, match.chunkId)
      const num = assignNum(key, metaByKey.get(key))
      segments.push({ type: 'ref', num })
    } else if (match.kind === 'wiki') {
      const key = keyFromWiki(match.slug)
      const num = assignNum(key, metaByKey.get(key))
      segments.push({ type: 'ref', num })
    } else if (match.kind === 'num' && match.num >= 1 && match.num <= metaList.length) {
      const c = metaList[match.num - 1]
      const key = citationKey(c)
      if (key) {
        const num = assignNum(key, c, match.num)
        segments.push({ type: 'ref', num })
      } else {
        segments.push({ type: 'text', value: content.slice(match.index, match.index + match.length) })
      }
    } else if (match.kind === 'num') {
      segments.push({ type: 'text', value: content.slice(match.index, match.index + match.length) })
    }

    cursor = match.index + match.length
  }

  if (cursor < content.length) {
    segments.push({ type: 'text', value: content.slice(cursor) })
  }

  if (segments.length === 0 && content) {
    segments.push({ type: 'text', value: content })
  }

  for (const c of metaList) {
    const key = citationKey(c)
    if (key && !keyToNum.has(key)) {
      assignNum(key, c)
    }
  }

  registry.sort((a, b) => a.num - b.num)

  return { registry, segments }
}

/**
 * @param {{ type?: string, label?: string, document_id?: string, chunk_id?: string, slug?: string, library?: string }} citation
 * @returns {string}
 */
export function formatReferenceLine(citation) {
  if (!citation) return ''
  if (citation.type === 'wiki' && citation.slug) {
    return `${citation.label || citation.slug} · wiki/${citation.slug}`
  }
  const parts = [citation.label || 'Document']
  if (citation.library === 'personal') parts.push('personal')
  else if (citation.library === 'org') parts.push('org')
  if (citation.document_id) {
    parts.push(`doc ${citation.document_id}`)
  }
  if (citation.chunk_id) {
    parts.push(`chunk ${citation.chunk_id}`)
  }
  return parts.join(' · ')
}
